import { z } from "zod";
import { email, expectedVersion, id, slug } from "./common";
import { VISIBILITIES } from "./visibility";
import { safeText } from "./text";
import { WORKSPACE_ROLES } from "./roles";

export const workspaceRole = z.enum(WORKSPACE_ROLES);

export const createWorkspaceInput = z.object({
  name: safeText(2, 80),
  slug,
});

/** ISO weekdays: 1 is Monday, 7 is Sunday. */
export const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;
export const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const;

/** Whether a YYYY-MM-DD date falls on one of these ISO weekdays. */
export const isWorkingDay = (date: string, workingDays: readonly number[]) => {
  const weekday = new Date(`${date}T00:00:00Z`).getUTCDay();
  return workingDays.includes(weekday === 0 ? 7 : weekday);
};

/** Send the rules that change. Default visibility only preselects the forms. */
export const updateWorkspacePolicyInput = z
  .object({
    requireMfa: z.boolean().optional(),
    defaultVisibility: z.enum(VISIBILITIES).optional(),
    workingDays: z
      .array(z.number().int().min(1).max(7))
      .min(1)
      .max(7)
      .refine((days) => new Set(days).size === days.length, "Each day only once")
      .optional(),
    expectedVersion,
  })
  .refine(
    (p) =>
      p.requireMfa !== undefined ||
      p.defaultVisibility !== undefined ||
      p.workingDays !== undefined,
    "Nothing to change",
  );
export type UpdateWorkspacePolicyInput = z.infer<typeof updateWorkspacePolicyInput>;

export const inviteMemberInput = z.object({
  email,
  role: workspaceRole,
});

export const revokeInvitationInput = z.object({ invitationId: id });

export const invitationTokenInput = z.object({
  token: z.string().min(32).max(128),
});

export const changeMemberRoleInput = z.object({
  membershipId: id,
  role: workspaceRole,
  expectedVersion,
});

export const removeMemberInput = z.object({ membershipId: id });
